import { useQuery } from "@tanstack/react-query";
import { fetchIsSupportAdmin, fetchMaintenanceState, type MaintenanceState } from "@/api/maintenance";

export const MAINTENANCE_QUERY_KEY = ["app-maintenance"] as const;

/**
 * État du mode maintenance global.
 *
 * Sondage court (20 s) plutôt que Realtime : la table n'est pas publiée et
 * l'état doit rester lisible même sans session (écran public de maintenance).
 * Fail-open : en cas d'erreur de lecture, l'app reste utilisable.
 */
export function useMaintenanceMode() {
  const { data, isLoading } = useQuery({
    queryKey: MAINTENANCE_QUERY_KEY,
    queryFn: async (): Promise<MaintenanceState | null> => {
      const { data, error } = await fetchMaintenanceState();
      if (error) throw error;
      return data as MaintenanceState | null;
    },
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    retry: 1,
  });

  const now = Date.now();
  const announceFrom = data?.announce_from ? new Date(data.announce_from).getTime() : null;
  const startsAt = data?.starts_at ? new Date(data.starts_at).getTime() : null;
  const endsAt = data?.ends_at ? new Date(data.ends_at).getTime() : null;

  const isMaintenanceActive = Boolean(data?.enabled);
  const isAnnounced =
    !isMaintenanceActive &&
    announceFrom !== null &&
    now >= announceFrom &&
    (endsAt === null || now <= endsAt);

  return {
    state: data ?? null,
    isLoading,
    isMaintenanceActive,
    isAnnounced,
    startsAt,
    endsAt,
  };
}

/** L'utilisateur courant est-il administrateur support (bypass maintenance) ? */
export function useIsSupportAdmin(userId: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ["is-support-admin", userId],
    queryFn: async () => {
      const { data, error } = await fetchIsSupportAdmin(userId!);
      if (error) throw error;
      return Boolean(data);
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
  return Boolean(data);
}
